import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'store/hooks';
import { getProfile } from 'store/auth/actions';
import { ActionType } from 'store/auth/model';
import { storage } from 'utils';
import type { AuthFactorType } from '@repo/types';
import { TwoFactorChallenge } from 'components/auth';
import { consumeReturnPath } from 'utils/postAuthReturn';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // VRG-490 §2 — the owner-facing SSO callback (`customerWorkOSCallbackUseCase
  // .execute`) currently fails closed on a verified factor rather than
  // carrying `challengeRequired` through this redirect (see its own
  // docblock) — Magic Auth is disabled account-wide, so this branch is not
  // reachable via THAT specific path today. Handled anyway: any redirect
  // that reaches here with these params (present or future) must route to
  // the challenge screen, never assume `token=` is present.
  const [challenge, setChallenge] = useState<{ ticket: string; factorType: AuthFactorType } | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      console.log('[AuthCallback] Starting authentication callback processing');
      console.log('[AuthCallback] Current URL:', window.location.href);

      try {
        const token = searchParams.get('token');
        const refreshToken = searchParams.get('refreshToken');
        const scanToken = searchParams.get('scanToken');
        const errorParam = searchParams.get('error');
        const isNewUser = searchParams.get('isNewUser') === 'true';
        const challengeRequired = searchParams.get('challengeRequired') === 'true';
        const ticket = searchParams.get('ticket');
        const factorType = searchParams.get('factorType') as AuthFactorType | null;

        console.log('[AuthCallback] URL params:', {
          hasToken: !!token,
          hasRefreshToken: !!refreshToken,
          hasScanToken: !!scanToken,
          hasError: !!errorParam,
          isNewUser,
          challengeRequired,
        });

        if (errorParam) {
          console.error('[AuthCallback] Error in callback:', decodeURIComponent(errorParam));
          setError(decodeURIComponent(errorParam));
          setLoading(false);
          setTimeout(() => navigate('/sign-in'), 3000);
          return;
        }

        // VRG-490 §2 — pause here; TwoFactorChallenge completes the login
        // itself via POST /auth/2fa/challenge/verify. No auto-redirect.
        if (challengeRequired && ticket && factorType) {
          setChallenge({ ticket, factorType });
          setLoading(false);
          return;
        }

        if (!token) {
          console.error('[AuthCallback] No token received in callback');
          setError('No authentication token received');
          setLoading(false);
          setTimeout(() => navigate('/sign-in'), 3000);
          return;
        }

        console.log('[AuthCallback] Storing tokens in storage');
        // Store tokens
        storage.setItem('accessToken', token);
        if (refreshToken) {
          storage.setItem('refreshToken', refreshToken);
          console.log('[AuthCallback] Refresh token stored');
        }
        if (scanToken) {
          storage.setItem('scanToken', scanToken);
          console.log('[AuthCallback] Scan token stored');
        }

        console.log('[AuthCallback] Invalidating React Query cache');
        // Invalidate all queries to refetch with new auth
        await queryClient.invalidateQueries();

        console.log('[AuthCallback] Updating auth state in Redux store');
        dispatch({ type: ActionType.SET_AUTH, payload: true });
        dispatch(getProfile());

        // Restore context from sessionStorage (if it was saved before OAuth)
        const embeddKey = sessionStorage.getItem('embeddKey');
        const companyId = sessionStorage.getItem('companyId');

        console.log('[AuthCallback] Session context:', {
          embeddKey: embeddKey || 'none',
          companyId: companyId || 'none',
        });

        // Clear session
        sessionStorage.removeItem('embeddKey');
        sessionStorage.removeItem('companyId');

        // VRG-720: a sign-in started FROM a page (the certificate access form's "Continue
        // with Google") records where to come back to. Absent -> the old default.
        const returnTo = consumeReturnPath();
        console.log('[AuthCallback] Attempting navigation to', returnTo ?? '/certificates');
        navigate(returnTo ?? '/certificates');
        console.log('[AuthCallback] Navigation call completed');
      } catch (err) {
        console.error('[AuthCallback] Callback error:', err);
        setError('Authentication failed');
        setLoading(false);
        setTimeout(() => navigate('/sign-in'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, queryClient, dispatch]);

  // VRG-490 §2 — the terminal state for this redirect, not a transient step
  // on the way to one; checked before `loading` since setting it also
  // clears `loading`.
  if (challenge) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '24px 16px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <TwoFactorChallenge
            ticket={challenge.ticket}
            factorType={challenge.factorType}
            onBack={() => navigate('/sign-in')}
            onVerified={() => navigate('/certificates')}
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div>
          <div style={{ marginBottom: '16px', textAlign: 'center' }}>
            Completing authentication...
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner" style={{
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px', color: '#e74c3c' }}>
            Authentication Error
          </div>
          <div style={{ marginBottom: '16px', color: '#666' }}>
            {error}
          </div>
          <div style={{ fontSize: '14px', color: '#999' }}>
            Redirecting to sign in...
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
